import { useEffect, useState } from 'react';
import {
    CategoriaOption,
    CentroAlert,
    CentroFormData,
    DetalleOption,
    MarcaOption,
    ModeloOption,
    ServicioAgregado,
    SubcategoriaOption,
} from '../types/centro.types';
import { CentroService } from '../services/CentroService';

export function useCentroCatalogos(
    formData: CentroFormData,
    setFormData: React.Dispatch<React.SetStateAction<CentroFormData>>,
    setAlert: React.Dispatch<React.SetStateAction<CentroAlert | null>>,
    categorias: CategoriaOption[],
    marcas: MarcaOption[],
) {
    const [subcategorias, setSubcategorias] = useState<SubcategoriaOption[]>([]);
    const [detalles, setDetalles] = useState<DetalleOption[]>([]);
    const [modelos, setModelos] = useState<ModeloOption[]>([]);

    const [selectedCat, setSelectedCat] = useState('');
    const [selectedSub, setSelectedSub] = useState('');
    const [selectedDet, setSelectedDet] = useState('');
    const [selectedMarca, setSelectedMarca] = useState('ALL');
    const [selectedModelo, setSelectedModelo] = useState('ALL');

    useEffect(() => {
        if (!selectedCat) {
            setSubcategorias([]);
            setSelectedSub('');
            setSelectedDet('');
            setDetalles([]);
            return;
        }
        CentroService.loadSubcategorias(selectedCat)
            .then(setSubcategorias)
            .catch(() => setSubcategorias([]));
        setSelectedSub('');
        setSelectedDet('');
        setDetalles([]);
    }, [selectedCat]);

    useEffect(() => {
        if (!selectedCat || !selectedSub) {
            setDetalles([]);
            setSelectedDet('');
            return;
        }
        CentroService.loadDetalles(selectedCat, selectedSub)
            .then(setDetalles)
            .catch(() => setDetalles([]));
        setSelectedDet('');
    }, [selectedSub, selectedCat]);

    useEffect(() => {
        if (!selectedMarca || selectedMarca === 'ALL') {
            setModelos([]);
            setSelectedModelo('ALL');
            return;
        }
        CentroService.loadModelos(selectedMarca)
            .then(setModelos)
            .catch(() => setModelos([]));
        setSelectedModelo('ALL');
    }, [selectedMarca]);

    const getCategoriaNombre = (id: string) => {
        const cat = categorias.find((c) => String(c.id ?? c.clave_categoria) === id);
        return cat?.nombre ?? cat?.categoria ?? '';
    };

    const handleAddService = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!selectedCat) {
            setAlert({ type: 'warning', message: 'Seleccione una Categoría de servicio.' });
            return;
        }

        const catName = getCategoriaNombre(selectedCat);
        const subName = selectedSub
            ? subcategorias.find((s) => String(s.clave_subcategoria) === selectedSub)?.subcategoria ?? ''
            : 'Todas las subcategorías';
        const detName = selectedDet
            ? detalles.find((d) => String(d.clave_subcategoria_detalle) === selectedDet)?.titulo ?? ''
            : selectedSub
              ? 'Todos los detalles'
              : '';
        const marcaName =
            selectedMarca === 'ALL'
                ? 'Todas las marcas'
                : marcas.find((m) => String(m.clave_marca) === selectedMarca)?.marca ?? '';
        const modeloName =
            selectedModelo === 'ALL'
                ? 'Todos los modelos'
                : modelos.find((m) => String(m.clave_modelo) === selectedModelo)?.modelo ?? '';

        const isDuplicate = formData.servicios.some(
            (s) =>
                s.idCategoria === selectedCat &&
                s.idSubcategoria === selectedSub &&
                s.idDetalle === selectedDet &&
                s.idMarca === selectedMarca &&
                s.idModelo === selectedModelo,
        );

        if (isDuplicate) {
            setAlert({ type: 'warning', message: 'Servicio duplicado.' });
            return;
        }

        const nuevo: ServicioAgregado = {
            idCategoria: selectedCat,
            categoria: catName,
            idSubcategoria: selectedSub,
            subcategoria: subName,
            idDetalle: selectedDet,
            detalle: detName,
            idMarca: selectedMarca,
            marca: marcaName,
            idModelo: selectedModelo,
            modelo: modeloName,
        };

        setFormData((prev) => ({ ...prev, servicios: [...prev.servicios, nuevo] }));
        setSelectedCat('');
        setSelectedMarca('ALL');
    };

    const handleRemoveService = (index: number, e: React.MouseEvent) => {
        e.preventDefault();
        setFormData((prev) => ({
            ...prev,
            servicios: prev.servicios.filter((_, i) => i !== index),
        }));
    };

    return {
        subcategorias,
        detalles,
        modelos,
        selectedCat,
        setSelectedCat,
        selectedSub,
        setSelectedSub,
        selectedDet,
        setSelectedDet,
        selectedMarca,
        setSelectedMarca,
        selectedModelo,
        setSelectedModelo,
        handleAddService,
        handleRemoveService,
    };
}
